import { camelCase, capitalize, lowerCase, snakeCase } from "lodash/fp";

type CamelCase<S extends string> = S extends `${infer T}_${infer U}`
  ? `${T}${Capitalize<CamelCase<U>>}`
  : S extends `${infer T}-${infer U}`
    ? `${T}${Capitalize<CamelCase<U>>}`
    : S extends `${infer T} ${infer U}`
      ? `${T}${Capitalize<CamelCase<U>>}`
      : S;

// Recursive type to camelCase keys in an object
export type CamelCasedProperties<T> = {
  [K in keyof T as CamelCase<string & K>]: T[K] extends object
    ? CamelCasedProperties<T[K]>
    : T[K];
};

export const parseIntOrDefault = (
  value: unknown,
  defaultValue = -1,
): number => {
  if (typeof value === "number") {
    return isFinite(value) ? value : defaultValue;
  }
  if (typeof value === "string") {
    const parsedValue = parseInt(value);
    return isFinite(parsedValue) ? parsedValue : defaultValue;
  }
  return defaultValue;
};

// skip whitespace will skip keys that have whitespace
export const transformKeys =
  <T>(keyFunc: (key: string) => string, skipWhitespace = false) =>
  (obj: T): T => {
    if (Array.isArray(obj)) {
      return obj.map(transformKeys(keyFunc, skipWhitespace)) as T;
    } else if (obj != null && obj.constructor === Object) {
      return Object.keys(obj).reduce(
        (result, key) => ({
          ...result,
          [skipWhitespace && key.includes(" ") ? key : keyFunc(key)]:
            transformKeys(keyFunc, skipWhitespace)(obj[key as keyof T]),
        }),
        {},
      ) as T;
    }
    return obj;
  };

// TODO: better typing here since the return type is not the same as the input type
// use the CamelCasedProperties type to ensure the return type is correct
// and do the same for snakeKeys
export const camelizeKeys: <T>(obj: T) => T = transformKeys(camelCase, true);
export const snakeKeys: <T>(object: T) => T = transformKeys(snakeCase, true);

/**
 * Strips the protocol from a URL for a friendlier display value.
 * (e.g., "https://app.read.ai/foo" => "app.read.ai/foo")
 * */
export const getDisplayUrl = (linkUrl: string) =>
  linkUrl.replace(/https?:\/\//, "");

/** @deprecated no replacement yet available */
export const startCase = (item: string) => item.replace(/\w+/g, capitalize);

/** @deprecated no replacement yet available */
export const titleize = (item: string) => {
  return startCase(lowerCase(item));
};

export const getStringType = (value: unknown): string => {
  if (typeof value === "string") return value;
  return "";
};
